import { HackleUser, Identifiers, IdentifierType, resolveIdentifiers, User } from "../../model/model"
import WorkspaceEvaluation from "./WorkspaceEvaluation"
import {
  BaseEvaluationDto,
  EvaluateEntityDto,
  WorkspaceEvaluationContextDto,
  WorkspaceEvaluationDto
} from "./model/dto"
import { WorkspaceContext } from "../WorkspaceContext"

// Session/device identifiers are transient and would fragment the per-user cache, so they are
// dropped when deriving the cache key (invariant: cache key = identifiers minus these).
const EXCLUDED = new Set<string>([IdentifierType.SESSION, IdentifierType.HACKLE_DEVICE])

export class WorkspaceEvaluationContextKey {
  constructor(readonly identifiers: Identifiers) {}

  hashKey(): string {
    // Unambiguous serialization: JSON escapes so identifier values containing `&`/`=` cannot
    // collide across different identifier sets.
    return JSON.stringify(
      Object.keys(this.identifiers)
        .sort()
        .map((key) => [key, this.identifiers[key]])
    )
  }
}

export class WorkspaceEvaluationContext implements WorkspaceContext {
  constructor(
    readonly key: WorkspaceEvaluationContextKey,
    readonly dto: WorkspaceEvaluationDto,
    readonly workspace: WorkspaceEvaluation,
    readonly fullEvaluatedAt: number
  ) {}

  static from(dto: WorkspaceEvaluationContextDto): WorkspaceEvaluationContext {
    return WorkspaceEvaluationContext.of(
      new WorkspaceEvaluationContextKey(dto.key),
      dto.evaluation,
      dto.fullEvaluatedAt
    )
  }

  static of(
    key: WorkspaceEvaluationContextKey,
    dto: WorkspaceEvaluationDto,
    fullEvaluatedAt: number
  ): WorkspaceEvaluationContext {
    return new WorkspaceEvaluationContext(key, dto, WorkspaceEvaluation.from(dto, fullEvaluatedAt), fullEvaluatedAt)
  }

  private static keyOfIdentifiers(identifiers: Identifiers): WorkspaceEvaluationContextKey {
    const result: Identifiers = {}
    for (const [type, value] of Object.entries(identifiers)) {
      if (!EXCLUDED.has(type)) {
        result[type] = value
      }
    }
    return new WorkspaceEvaluationContextKey(result)
  }

  static keyOf(user: HackleUser): WorkspaceEvaluationContextKey {
    return WorkspaceEvaluationContext.keyOfIdentifiers(user.identifiers)
  }

  static keyOfUser(user: User): WorkspaceEvaluationContextKey {
    return WorkspaceEvaluationContext.keyOfIdentifiers(resolveIdentifiers(user))
  }

  toDto(): WorkspaceEvaluationContextDto {
    return {
      key: this.key.identifiers,
      evaluation: this.dto,
      fullEvaluatedAt: this.fullEvaluatedAt
    }
  }

  toBaseEvaluationDto(): BaseEvaluationDto {
    const entities: EvaluateEntityDto[] = this.dto.results.map((it) => ({
      type: it.type,
      id: it.id,
      hash: it.hash
    }))
    return {
      fullEvaluatedAt: this.fullEvaluatedAt,
      metadata: this.dto.metadata,
      entities
    }
  }
}
